import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Button, Divider, List, ListItemButton, Popover, Typography,
} from '@mui/material';

import { useLanguage } from '../../contexts/LanguageContext';
import { getNotifications, markAllAsRead } from '../../services/notificationService';
import LigneNotification, { fondNonLue } from './LigneNotification';

/** Combien de lignes le panneau montre. La page montre le reste. */
export const LIGNES_DU_PANNEAU = 4;

/**
 * Le panneau déroulant de la cloche.
 *
 * 🔴 LA CLOCHE N'ÉTAIT PAS INERTE : ELLE NAVIGUAIT. `handleNotificationsClick`
 * faisait `navigate('/notifications')` — le badge annonçait donc un nombre, et
 * le clic quittait l'écran en cours pour aller le chercher. **On ne pouvait
 * atteindre ses notifications qu'en abandonnant ce qu'on faisait.**
 *
 * 🔴 IL NE CHARGE QUE QUATRE LIGNES, et c'est la route qui les coupe. La route
 * accepte `per_page` **depuis toujours** — vérifié. Charger la page entière pour
 * en montrer quatre ferait payer vingt lignes à chaque ouverture d'un panneau
 * qu'on referme aussitôt. ⚠️ *Un panneau de survol qui coûte le prix d'un écran
 * est un écran qu'on ouvre par accident.*
 *
 * ⚠️ IL NE CHARGE QU'À L'OUVERTURE, jamais au montage : monté dans l'entête, il
 * est présent sur TOUS les écrans. Charger au montage ferait une requête par
 * navigation, pour une liste que personne ne regarde.
 */
const PanneauNotifications = ({ ancre, onFermer, onNaviguer, onChangement,
                                showSnackbar }) => {
  const { t } = useLanguage();
  const [lignes, setLignes] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState(null);
  const ouvert = Boolean(ancre);

  const charger = useCallback(async () => {
    if (!ouvert) return;
    try {
      setChargement(true);
      // ⚠️ `per_page` À 4, PAS UN `slice` CÔTÉ CLIENT. Couper après avoir tout
      // reçu ne coupe que l'affichage — c'est la leçon D2 du lot RU5-e : *un tri
      // qui vit dans un slice n'est pas un tri*, et une limite non plus.
      const data = await getNotifications({ page: 1, per_page: LIGNES_DU_PANNEAU });
      setLignes(data.notifications || []);
      setErreur(null);
    } catch (e) {
      // L'état ne porte qu'une CLÉ — la traduction se fait au rendu.
      setErreur('notifications.loadError');
      setLignes([]);
    } finally {
      setChargement(false);
    }
  }, [ouvert]);

  useEffect(() => { charger(); }, [charger]);

  const toutMarquerLu = async () => {
    try {
      await markAllAsRead();
      await charger();
      if (onChangement) onChangement();
    } catch (e) {
      if (showSnackbar) showSnackbar(t('notifications.markingError'), 'error');
    }
  };

  const nonLuesVisibles = lignes.filter((n) => !n.is_read).length;

  return (
    <Popover
      open={ouvert}
      anchorEl={ancre}
      onClose={onFermer}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      slotProps={{ paper: { sx: { width: 380, maxWidth: '100vw' } } }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.5 }}>
        <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1 }}>
          {t('notifications.title')}
        </Typography>
        {/* ⚠️ DÉSACTIVÉ QUAND IL N'Y A RIEN À MARQUER — *un bouton qui ne fait
            rien est pire qu'un bouton absent : il promet.* Et pas de dialogue de
            confirmation ici, contrairement à la page : le panneau ne montre que
            quatre lignes, le geste porte sur ce qu'on voit. */}
        <Button size="small" onClick={toutMarquerLu} disabled={nonLuesVisibles === 0}>
          {t('notifications.markAllAsRead')}
        </Button>
      </Box>
      <Divider />

      {chargement ? (
        <Box sx={{ p: 3 }}>
          <Typography variant="body2" color="text.secondary">
            {t('common.loading')}
          </Typography>
        </Box>
      ) : erreur ? (
        <Box sx={{ p: 3 }}>
          <Typography variant="body2" color="error.main">{t(erreur)}</Typography>
        </Box>
      ) : lignes.length === 0 ? (
        <Box sx={{ p: 3 }}>
          <Typography variant="body2" color="text.secondary">
            {t('notifications.noNotificationsFound')}
          </Typography>
        </Box>
      ) : (
        <List disablePadding>
          {lignes.map((n) => (
            <ListItemButton
              key={n.id}
              onClick={() => { onFermer(); onNaviguer('/notifications'); }}
              sx={{ alignItems: 'flex-start', py: 1.25, ...fondNonLue(!n.is_read) }}
            >
              <LigneNotification notification={n} dense />
            </ListItemButton>
          ))}
        </List>
      )}

      <Divider />
      {/* 🔴 LE LIEN VERS LA PAGE EST TOUJOURS LÀ, même quand le panneau est vide :
          c'est lui qui dit que le panneau n'est qu'un extrait. Sans lui, quatre
          lignes ressembleraient à la totalité. */}
      <Box sx={{ p: 1 }}>
        <Button fullWidth size="small"
                onClick={() => { onFermer(); onNaviguer('/notifications'); }}>
          {t('notifications.seeAll')}
        </Button>
      </Box>
    </Popover>
  );
};

export default PanneauNotifications;
